"use client";

import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { trackClick } from '@/utils/trackingUtils';
import QuoteForm from './adHero/components/QuoteForm';

interface ContactFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber?: string;
}

const ContactFormDrawer: React.FC<ContactFormDrawerProps> = ({
  isOpen,
  onClose,
  phoneNumber = "0477746329"
}) => {
  // Handle close with tracking
  const handleClose = () => {
    trackClick("book_trailer", "contact-drawer-close-button");
    onClose();
  };

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - Mobile Only */}
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 transition-opacity duration-300 md:hidden"
        onClick={handleBackdropClick}
      />
      
      {/* Drawer */}
      <div className="fixed bottom-0 left-0 right-0 md:left-auto md:right-0 md:w-[480px] z-50 animate-in slide-in-from-bottom md:slide-in-from-right duration-500">
        <div 
          className="bg-white rounded-t-3xl md:rounded-none shadow-2xl h-[85vh] md:h-[90vh] flex flex-col overflow-hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {/* Drawer Handle - Mobile Only */}
          <div className="flex justify-center py-3 bg-white rounded-t-3xl md:hidden">
            <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
          </div>

          {/* Header with Title and Close Button */}
          <div className="flex items-center justify-between px-4 pb-4 md:pt-4 bg-white flex-shrink-0">
            <h2 className="text-xl font-bold text-green-700 flex-1">
              Get Your Free Quote
            </h2>
            
            {/* Custom Close Button */}
            <button
              onClick={handleClose}
              className="w-12 h-12 rounded-full bg-green-800 hover:bg-green-900 active:bg-green-950 flex items-center justify-center transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-green-300 shadow-lg ml-4 border-2 border-green-700"
              aria-label="Close contact form"
            >
              <X className="w-6 h-6 text-white" strokeWidth={3} />
            </button>
          </div>
          
          {/* Scrollable Form Content */}
          <div 
            className="flex-1 overflow-y-auto p-4 bg-gray-50"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              paddingBottom: `calc(1rem + env(safe-area-inset-bottom))`
            }}
          >
            <QuoteForm
              formTitle=""
              phoneNumber={phoneNumber}
              isHighlighted={false}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactFormDrawer;